import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import IconPickerGrid from '@/components/Common/IconPickerGrid';
import {
    FaHeadset, FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaSearch,
    FaFolder, FaFolderOpen, FaSitemap, FaShieldAlt, FaClock,
    FaBook, FaStar, FaChartBar, FaSync, FaChevronDown, FaChevronRight,
    FaSpinner, FaExclamationTriangle, FaCheckCircle,
    FaBold, FaItalic, FaListUl, FaListOl, FaCode, FaQuoteRight,
    FaHeading, FaMinus, FaEye, FaPen, FaGlobe, FaLock, FaTag,
    FaLaptop, FaServer, FaDesktop, FaPrint, FaWifi, FaDatabase,
    FaEnvelope, FaUserShield, FaNetworkWired, FaHdd, FaMobile,
    FaCloud, FaBug, FaKey, FaCog, FaTools, FaFileAlt, FaPhone,
    FaProjectDiagram, FaUserCog, FaShoppingCart, FaClipboardList
} from 'react-icons/fa';
import TemplateRenderer from '@/components/ActionItems/TemplateRenderer';
import './ITServiceDeskPage.scss';
import * as Sentry from '@sentry/react';

// ────────────────────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────────────────────

const TABS = [
    { key: 'categories', label: 'Ticket Types', icon: FaSitemap },
    { key: 'sla', label: 'SLA Policies', icon: FaShieldAlt },
    { key: 'kb', label: 'Knowledge Base', icon: FaBook },
    { key: 'csat', label: 'CSAT Reports', icon: FaStar },
    { key: 'recurring', label: 'Recurring Tickets', icon: FaSync },
    { key: 'email', label: 'Email Accounts', icon: FaEnvelope },
];

const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];

/**
 * Curated list of React Icons available for category icon selection.
 * Each entry maps a storage key to a component and label.
 */
const ICON_OPTIONS = [
    { value: '', label: 'None', icon: null },
    { value: 'FaLaptop', label: 'Laptop', icon: FaLaptop },
    { value: 'FaDesktop', label: 'Desktop', icon: FaDesktop },
    { value: 'FaServer', label: 'Server', icon: FaServer },
    { value: 'FaPrint', label: 'Printer', icon: FaPrint },
    { value: 'FaMobile', label: 'Mobile', icon: FaMobile },
    { value: 'FaHdd', label: 'Storage/Disk', icon: FaHdd },
    { value: 'FaWifi', label: 'Wi-Fi', icon: FaWifi },
    { value: 'FaNetworkWired', label: 'Network', icon: FaNetworkWired },
    { value: 'FaCloud', label: 'Cloud', icon: FaCloud },
    { value: 'FaDatabase', label: 'Database', icon: FaDatabase },
    { value: 'FaEnvelope', label: 'Email', icon: FaEnvelope },
    { value: 'FaPhone', label: 'Phone', icon: FaPhone },
    { value: 'FaShieldAlt', label: 'Security', icon: FaShieldAlt },
    { value: 'FaUserShield', label: 'User Security', icon: FaUserShield },
    { value: 'FaKey', label: 'Access/Keys', icon: FaKey },
    { value: 'FaBug', label: 'Bug', icon: FaBug },
    { value: 'FaTools', label: 'Tools', icon: FaTools },
    { value: 'FaCog', label: 'Settings', icon: FaCog },
    { value: 'FaUserCog', label: 'User Config', icon: FaUserCog },
    { value: 'FaFileAlt', label: 'Document', icon: FaFileAlt },
    { value: 'FaBook', label: 'Knowledge', icon: FaBook },
    { value: 'FaProjectDiagram', label: 'Project', icon: FaProjectDiagram },
    { value: 'FaClipboardList', label: 'Checklist', icon: FaClipboardList },
    { value: 'FaShoppingCart', label: 'Procurement', icon: FaShoppingCart },
    { value: 'FaFolder', label: 'Folder', icon: FaFolder },
    { value: 'FaTag', label: 'Tag', icon: FaTag },
    { value: 'FaExclamationTriangle', label: 'Warning', icon: FaExclamationTriangle },
    { value: 'FaHeadset', label: 'Support', icon: FaHeadset },
];

/**
 * Same icons shaped for the visual IconPickerGrid component.
 * Entries use the same storage key (`value`) so existing DB data is compatible.
 */
const ICON_PICKER_OPTIONS = ICON_OPTIONS
    .filter(o => o.icon)
    .map(o => ({ value: o.value, label: o.label, Icon: o.icon }));

/**
 * Lookup map for rendering a stored icon key as a React component.
 */
const ICON_MAP = Object.fromEntries(ICON_OPTIONS.filter(o => o.icon).map(o => [o.value, o.icon]));

/**
 * Convert a Font Awesome class name (e.g., 'fa-headset') to a React Icon key (e.g., 'FaHeadset').
 */
const faClassToReactKey = (faClass) => {
    if (!faClass || !faClass.startsWith('fa-')) return faClass;
    return 'Fa' + faClass.slice(3).split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('');
};

const emptyCategoryForm = () => ({
    name: '', description: '', icon: '', color: '#4a90d9',
    parentCategoryId: null, departmentId: null,
    defaultSlaPolicyId: null, defaultAssigneeUserId: null,
    defaultAssigneeFunctionId: null, sortOrder: 0
});

const emptySlaForm = () => ({
    name: '', description: '',
    responseTimeLowMinutes: 1440, responseTimeMediumMinutes: 480,
    responseTimeHighMinutes: 120, responseTimeCriticalMinutes: 30,
    resolutionTimeLowMinutes: 10080, resolutionTimeMediumMinutes: 2880,
    resolutionTimeHighMinutes: 480, resolutionTimeCriticalMinutes: 120,
    businessHoursOnly: true, businessHoursStart: '08:00', businessHoursEnd: '17:00',
    businessDays: '1,2,3,4,5', autoEscalateOnBreach: true,
    notifyOnWarning: true, warningThresholdPercent: 75, isDefault: false
});

const emptyKbForm = () => ({
    title: '', content: '', summary: '', categoryId: null,
    tags: '', visibility: 'Internal', status: 'Draft'
});

const emptyRecurringForm = () => ({
    name: '', description: '', cronExpression: '0 9 * * 1',
    timeZone: 'Africa/Johannesburg', propertyId: 1,
    ticketTitle: '', ticketDescription: '', ticketPriority: 'Medium',
    actionItemsType: '', templateKey: '',
    categoryId: null, assignToUserId: null, assignToDepartmentId: null,
    assignToFunctionId: null, boardId: null, tags: '',
    dueDaysFromCreation: null, endDate: null,
    templateDataJson: null
});

// ────────────────────────────────────────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────────────────────────────────────────

const ITServiceDeskPage = ({ initialTab = 'categories' }) => {
    const { apiFetch, user } = useAuth();
    const [activeTab, setActiveTab] = useState(initialTab);

    return (
        <div className="it-service-desk-page">
            <div className="itsd-page-header">
                <div className="itsd-header-left">
                    <h1><FaHeadset /> IT Service Desk Configuration</h1>
                    <p className="itsd-header-desc">
                        Manage ticket types, SLA policies, knowledge base, satisfaction surveys, and recurring tickets.
                    </p>
                </div>
            </div>

            <div className="itsd-tabs">
                {TABS.map(tab => (
                    <button
                        key={tab.key}
                        className={`itsd-tab ${activeTab === tab.key ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        <tab.icon /> {tab.label}
                    </button>
                ))}
            </div>

            <div className="itsd-tab-content">
                {activeTab === 'categories' && <CategoriesTab apiFetch={apiFetch} user={user} />}
                {activeTab === 'sla' && <SlaPoliciesTab apiFetch={apiFetch} />}
                {activeTab === 'kb' && <KnowledgeBaseTab apiFetch={apiFetch} />}
                {activeTab === 'csat' && <CsatReportsTab apiFetch={apiFetch} />}
                {activeTab === 'recurring' && <RecurringTicketsTab apiFetch={apiFetch} />}
                {activeTab === 'email' && <EmailAccountsTab apiFetch={apiFetch} />}
            </div>
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════════
// CATEGORIES TAB
// ════════════════════════════════════════════════════════════════════════════════

const CategoriesTab = ({ apiFetch, user }) => {
    const [itContexts, setItContexts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingContextId, setEditingContextId] = useState(null);
    const [form, setForm] = useState(emptyCategoryForm());
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [slaPolicies, setSlaPolicies] = useState([]);

    // Load IT service types from Module Template Assignments (include inactive for two-phase delete)
    const loadItContexts = useCallback(async () => {
        try {
            const res = await apiFetch('/api/ModuleTemplateAssignments?moduleKey=IT&includeInactive=true');
            if (res.ok) {
                const json = await res.json();
                if (json.success && json.data?.length > 0) {
                    const itModule = json.data.find(m => m.moduleKey === 'IT');
                    setItContexts(itModule?.contexts || []);
                }
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            console.error('Failed to load IT contexts:', catchError);
        }
    }, [apiFetch]);

    // Load sub-categories from TicketCategories table
    const loadCategories = useCallback(async () => {
        try {
            const res = await apiFetch('/api/ITServiceDesk/categories?activeOnly=false');
            if (res.ok) {
                const json = await res.json();
                if (json.success) setCategories(json.data || []);
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            /* silent - sub-categories are optional */
        }
    }, [apiFetch]);

    const loadSlaPolicies = useCallback(async () => {
        try {
            const res = await apiFetch('/api/ITServiceDesk/sla-policies');
            if (res.ok) {
                const json = await res.json();
                if (json.success) setSlaPolicies(json.data || []);
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            /* silent */
        }
    }, [apiFetch]);

    useEffect(() => {
        const loadAll = async () => {
            setLoading(true);
            await Promise.all([loadItContexts(), loadCategories(), loadSlaPolicies()]);
            setLoading(false);
        };
        loadAll();
    }, [loadItContexts, loadCategories, loadSlaPolicies]);

    const toggleExpand = (id) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    // Build category tree with IT contexts as top-level entries
    // and TicketCategories as sub-categories
    const categoryTree = useMemo(() => {
        // Map IT Module Template Assignment contexts as top-level categories
        const contextNodes = itContexts
            .filter(ctx => !search || ctx.contextDisplayName?.toLowerCase().includes(search.toLowerCase()))
            .map(ctx => ({
                categoryId: `ctx-${ctx.id}`,
                isContext: true,
                contextId: ctx.id,
                name: ctx.contextDisplayName,
                description: ctx.contextDescription,
                icon: ctx.icon,
                color: null,
                isActive: ctx.isActive !== false,
                sortOrder: ctx.sortOrder || 0,
                children: categories
                    .filter(c => c.parentCategoryId == null &&
                        (!search || c.name?.toLowerCase().includes(search.toLowerCase())))
                    .filter(() => false) // Sub-categories not linked yet; will show under flat list below
                    .map(c => ({ ...c, children: [] }))
            }));

        // Also include any standalone TicketCategories (not linked to a context)
        const standaloneCategories = categories
            .filter(c => !search || c.name?.toLowerCase().includes(search.toLowerCase()));

        // Build tree from standalone categories
        const map = {};
        const roots = [];
        standaloneCategories.forEach(c => { map[c.categoryId] = { ...c, children: [] }; });
        standaloneCategories.forEach(c => {
            if (c.parentCategoryId && map[c.parentCategoryId]) {
                map[c.parentCategoryId].children.push(map[c.categoryId]);
            } else {
                roots.push(map[c.categoryId]);
            }
        });

        return [...contextNodes, ...roots];
    }, [itContexts, categories, search]);

    const openCreateForm = (parentId = null) => {
        setEditingId(null);
        setEditingContextId(null);
        setForm({ ...emptyCategoryForm(), parentCategoryId: parentId });
        setShowForm(true);
    };

    const openEditForm = (cat) => {
        setEditingContextId(null);
        setEditingId(cat.categoryId);
        setForm({
            name: cat.name || '', description: cat.description || '',
            icon: cat.icon || '', color: cat.color || '#4a90d9',
            parentCategoryId: cat.parentCategoryId,
            departmentId: cat.departmentId,
            defaultSlaPolicyId: cat.defaultSlaPolicyId,
            defaultAssigneeUserId: cat.defaultAssigneeUserId,
            defaultAssigneeFunctionId: cat.defaultAssigneeFunctionId,
            sortOrder: cat.sortOrder || 0
        });
        setShowForm(true);
    };

    const openEditContextForm = (ctx) => {
        setEditingId(null);
        setEditingContextId(ctx.contextId);
        setForm({
            name: ctx.name || '', description: ctx.description || '',
            icon: ctx.icon || '', color: '#4a90d9',
            parentCategoryId: null, departmentId: null,
            defaultSlaPolicyId: null, defaultAssigneeUserId: null,
            defaultAssigneeFunctionId: null,
            sortOrder: ctx.sortOrder || 0
        });
        setShowForm(true);
    };

    /**
     * Convert a React Icon key (e.g., 'FaHeadset') to a FA class name (e.g., 'fa-headset').
     */
    const reactKeyToFaClass = (reactKey) => {
        if (!reactKey || reactKey.startsWith('fa-')) return reactKey;
        // FaHeadset -> fa-headset, FaNetworkWired -> fa-network-wired
        const withoutFa = reactKey.slice(2);
        return 'fa-' + withoutFa.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '');
    };

    const handleSave = async () => {
        if (!form.name.trim()) { toast.error('Name is required'); return; }
        setSaving(true);
        try {
            if (editingContextId) {
                // Editing an existing IT context (Module Template Assignment)
                const res = await apiFetch(`/api/ModuleTemplateAssignments/${editingContextId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        contextDisplayName: form.name,
                        contextDescription: form.description,
                        icon: reactKeyToFaClass(form.icon) || undefined,
                        sortOrder: form.sortOrder,
                        userId: user?.userID || 0
                    })
                });
                if (res.ok) {
                    toast.success('Service type updated');
                    setShowForm(false);
                    loadItContexts();
                } else {
                    const err = await res.json().catch(() => ({}));
                    toast.error(err.message || 'Update failed');
                }
            } else if (editingId) {
                // Editing an existing TicketCategory
                const res = await apiFetch(`/api/ITServiceDesk/categories/${editingId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(form)
                });
                if (res.ok) {
                    toast.success('Category updated');
                    setShowForm(false);
                    loadCategories();
                } else {
                    const err = await res.json().catch(() => ({}));
                    toast.error(err.message || 'Update failed');
                }
            } else if (!form.parentCategoryId) {
                // Creating a new top-level category → create as IT Module Template Assignment
                const contextKey = form.name.replace(/[^a-zA-Z0-9]/g, '');
                const res = await apiFetch('/api/ModuleTemplateAssignments', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        moduleKey: 'IT',
                        moduleDisplayName: 'IT',
                        contextKey: contextKey,
                        contextDisplayName: form.name,
                        contextDescription: form.description,
                        icon: reactKeyToFaClass(form.icon) || 'fa-cog',
                        sortOrder: form.sortOrder,
                        isActive: true,
                        userId: user?.userID || 0
                    })
                });
                if (res.ok) {
                    toast.success('Service type created');
                    setShowForm(false);
                    loadItContexts();
                } else {
                    const err = await res.json().catch(() => ({}));
                    toast.error(err.message || 'Create failed');
                }
            } else {
                // Creating a sub-category → create as TicketCategory
                const res = await apiFetch('/api/ITServiceDesk/categories', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(form)
                });
                if (res.ok) {
                    toast.success('Category created');
                    setShowForm(false);
                    loadCategories();
                } else {
                    const err = await res.json().catch(() => ({}));
                    toast.error(err.message || 'Create failed');
                }
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Save failed');
        }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        const cat = categories.find(c => c.categoryId === id);
        const isInactive = cat && !cat.isActive;
        const msg = isInactive
            ? 'This category is already inactive. Permanently delete it? This cannot be undone.'
            : 'Deactivate this category? You can permanently delete it afterwards.';
        if (!confirm(msg)) return;
        try {
            const res = await apiFetch(`/api/ITServiceDesk/categories/${id}`, { method: 'DELETE' });
            if (res.ok) {
                const json = await res.json().catch(() => ({}));
                toast.success(json.message || (isInactive ? 'Category permanently deleted' : 'Category deactivated'));
                loadCategories();
            }
            else { toast.error('Delete failed'); }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Delete failed');
        }
    };

    const handleContextDelete = async (ctx) => {
        const isInactive = !ctx.isActive;
        const msg = isInactive
            ? `Permanently delete "${ctx.name}"? This cannot be undone.`
            : `Deactivate "${ctx.name}"? You can permanently delete it afterwards.`;
        if (!confirm(msg)) return;
        try {
            const res = await apiFetch(`/api/ModuleTemplateAssignments/${ctx.contextId}?userId=${user?.userID || 0}`, { method: 'DELETE' });
            if (res.ok) {
                const json = await res.json().catch(() => ({}));
                toast.success(json.message || (isInactive ? 'Service type permanently deleted' : 'Service type deactivated'));
                loadItContexts();
            }
            else { toast.error('Delete failed'); }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Delete failed');
        }
    };

    const renderCategoryNode = (node, depth = 0) => {
        // Resolve icon: IT contexts use fa-class names, ticket categories use FaComponentName
        const iconKey = node.isContext ? faClassToReactKey(node.icon) : node.icon;
        const NodeIcon = iconKey && ICON_MAP[iconKey] ? ICON_MAP[iconKey] : null;
        const isContextNode = node.isContext === true;
        return (
            <div key={node.categoryId} className="itsd-category-node" style={{ marginLeft: depth * 24 }}>
                <div className="itsd-category-row">
                    <button className="itsd-expand-btn" onClick={() => toggleExpand(node.categoryId)}>
                        {node.children?.length > 0
                            ? (expandedIds.has(node.categoryId) ? <FaFolderOpen /> : <FaFolder />)
                            : (NodeIcon ? <NodeIcon /> : <FaTag />)}
                    </button>
                    <span className="itsd-category-color" style={{ background: node.color || '#4a90d9' }} />
                    <span className="itsd-category-name">{node.name}</span>
                    {isContextNode && node.description && (
                        <span className="itsd-category-path">{node.description}</span>
                    )}
                    {!isContextNode && node.fullPath && <span className="itsd-category-path">{node.fullPath}</span>}
                    {isContextNode && <span className="itsd-badge" style={{ background: '#e0edff', color: '#2563eb', fontSize: '0.7rem', padding: '2px 6px', borderRadius: 4, marginLeft: 8 }}>Service Type</span>}
                    {!node.isActive && <span className="itsd-badge inactive">Inactive</span>}
                    {isContextNode ? (
                        <div className="itsd-category-actions">
                            <button title="Edit" onClick={() => openEditContextForm(node)}><FaEdit /></button>
                            <button title={node.isActive ? 'Deactivate' : 'Permanently delete'} className="danger" onClick={() => handleContextDelete(node)}><FaTrash /></button>
                        </div>
                    ) : (
                        <div className="itsd-category-actions">
                            <button title="Add sub-category" onClick={() => openCreateForm(node.categoryId)}><FaPlus /></button>
                            <button title="Edit" onClick={() => openEditForm(node)}><FaEdit /></button>
                            <button title={node.isActive ? 'Deactivate' : 'Permanently delete'} className="danger" onClick={() => handleDelete(node.categoryId)}><FaTrash /></button>
                        </div>
                    )}
                </div>
                {expandedIds.has(node.categoryId) && node.children?.map(child => renderCategoryNode(child, depth + 1))}
            </div>
        );
    };

    if (loading) return <HexagonLoader />;

    return (
        <div className="itsd-section">
            <div className="itsd-section-toolbar">
                <div className="itsd-search-box">
                    <FaSearch />
                    <input placeholder="Search ticket types..." value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <button className="itsd-btn primary" onClick={() => openCreateForm()}>
                    <FaPlus /> Add Category
                </button>
            </div>

            {itContexts.length > 0 && (
                <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0 0 12px 0' }}>
                    Top-level ticket types are synced with <strong>Module Template Assignments</strong>. Use <strong>+ Add Category</strong> to create new service types or sub-categories.
                </p>
            )}

            {showForm && (
                <div className="itsd-form-card">
                    <h3>{editingContextId ? 'Edit Service Type' : editingId ? 'Edit Category' : 'New Category / Service Type'}</h3>
                    <div className="itsd-form-grid">
                        <label>Name *
                            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                        </label>
                        <label>Description
                            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                        </label>
                        <label>Icon
                            <IconPickerGrid
                                value={form.icon}
                                onChange={(iconValue) => setForm(f => ({ ...f, icon: iconValue }))}
                                options={ICON_PICKER_OPTIONS}
                            />
                        </label>
                        {!editingContextId && (
                            <label>Color
                                <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} />
                            </label>
                        )}
                        {!editingContextId && !editingId && (
                            <label>Parent Category
                                <select value={form.parentCategoryId || ''} onChange={e => setForm(f => ({ ...f, parentCategoryId: e.target.value ? parseInt(e.target.value) : null }))}>
                                    <option value="">None (Top Level — creates Service Type)</option>
                                    {categories.filter(c => c.categoryId !== editingId).map(c => (
                                        <option key={c.categoryId} value={c.categoryId}>{c.fullPath || c.name}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                        {!editingContextId && (
                            <label>Default SLA Policy
                                <select value={form.defaultSlaPolicyId || ''} onChange={e => setForm(f => ({ ...f, defaultSlaPolicyId: e.target.value ? parseInt(e.target.value) : null }))}>
                                    <option value="">None</option>
                                    {slaPolicies.map(p => (
                                        <option key={p.policyId} value={p.policyId}>{p.name}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                        <label>Sort Order
                            <input type="number" value={form.sortOrder} onChange={e => setForm(f => ({ ...f, sortOrder: parseInt(e.target.value) || 0 }))} />
                        </label>
                    </div>
                    <div className="itsd-form-actions">
                        <button className="itsd-btn primary" onClick={handleSave} disabled={saving}>
                            {saving ? <FaSpinner className="spin" /> : <FaSave />} Save
                        </button>
                        <button className="itsd-btn" onClick={() => setShowForm(false)}>
                            <FaTimes /> Cancel
                        </button>
                    </div>
                </div>
            )}

            <div className="itsd-category-tree">
                {categoryTree.length === 0
                    ? <p className="itsd-empty">No ticket types found. Create one to get started.</p>
                    : categoryTree.map(node => renderCategoryNode(node))
                }
            </div>
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════════
// SLA POLICIES TAB
// ════════════════════════════════════════════════════════════════════════════════

const SlaPoliciesTab = ({ apiFetch }) => {
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptySlaForm());
    const [saving, setSaving] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [slaReport, setSlaReport] = useState(null);
    const [showReport, setShowReport] = useState(false);

    const loadPolicies = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch('/api/ITServiceDesk/sla-policies');
            if (res.ok) {
                const json = await res.json();
                if (json.success) setPolicies(json.data || []);
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to load SLA policies');
        }
        finally { setLoading(false); }
    }, [apiFetch]);

    useEffect(() => { loadPolicies(); }, [loadPolicies]);

    const loadReport = async () => {
        try {
            const res = await apiFetch('/api/ITServiceDesk/sla-report');
            if (res.ok) {
                const json = await res.json();
                if (json.success) { setSlaReport(json.data); setShowReport(true); }
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to load SLA report');
        }
    };

    const openCreate = () => { setEditingId(null); setForm(emptySlaForm()); setShowForm(true); };
    const openEdit = (p) => {
        setEditingId(p.policyId);
        setForm({
            name: p.name, description: p.description || '',
            responseTimeLowMinutes: p.responseTimeLowMinutes,
            responseTimeMediumMinutes: p.responseTimeMediumMinutes,
            responseTimeHighMinutes: p.responseTimeHighMinutes,
            responseTimeCriticalMinutes: p.responseTimeCriticalMinutes,
            resolutionTimeLowMinutes: p.resolutionTimeLowMinutes,
            resolutionTimeMediumMinutes: p.resolutionTimeMediumMinutes,
            resolutionTimeHighMinutes: p.resolutionTimeHighMinutes,
            resolutionTimeCriticalMinutes: p.resolutionTimeCriticalMinutes,
            businessHoursOnly: p.businessHoursOnly,
            businessHoursStart: p.businessHoursStart,
            businessHoursEnd: p.businessHoursEnd,
            businessDays: p.businessDays,
            autoEscalateOnBreach: p.autoEscalateOnBreach,
            notifyOnWarning: p.notifyOnWarning,
            warningThresholdPercent: p.warningThresholdPercent,
            isDefault: p.isDefault
        });
        setShowForm(true);
    };

    const handleSave = async () => {
        if (!form.name.trim()) { toast.error('Name is required'); return; }
        setSaving(true);
        try {
            const url = editingId ? `/api/ITServiceDesk/sla-policies/${editingId}` : '/api/ITServiceDesk/sla-policies';
            const method = editingId ? 'PUT' : 'POST';
            const res = await apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
            if (res.ok) {
                toast.success(editingId ? 'Policy updated' : 'Policy created');
                setShowForm(false);
                loadPolicies();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.message || 'Save failed');
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Save failed');
        }
        finally { setSaving(false); }
    };

    const formatMinutes = (mins) => {
        if (mins < 60) return `${mins}m`;
        if (mins < 1440) return `${(mins / 60).toFixed(1)}h`;
        return `${(mins / 1440).toFixed(1)}d`;
    };

    if (loading) return <HexagonLoader />;

    return (
        <div className="itsd-section">
            <div className="itsd-section-toolbar">
                <button className="itsd-btn secondary" onClick={loadReport}>
                    <FaChartBar /> SLA Compliance Report
                </button>
                <button className="itsd-btn primary" onClick={openCreate}>
                    <FaPlus /> Add Policy
                </button>
            </div>

            {showReport && slaReport && (
                <div className="itsd-report-card">
                    <div className="itsd-report-header">
                        <h3>SLA Compliance Report</h3>
                        <button className="itsd-btn-icon" onClick={() => setShowReport(false)}><FaTimes /></button>
                    </div>
                    <div className="itsd-stats-row">
                        <div className="itsd-stat"><span className="itsd-stat-value">{slaReport.totalTracked}</span><span className="itsd-stat-label">Tracked</span></div>
                        <div className="itsd-stat"><span className="itsd-stat-value color-green">{slaReport.responseMetCount}</span><span className="itsd-stat-label">Response Met</span></div>
                        <div className="itsd-stat"><span className="itsd-stat-value color-red">{slaReport.responseBreachedCount}</span><span className="itsd-stat-label">Response Breached</span></div>
                        <div className="itsd-stat"><span className="itsd-stat-value color-green">{slaReport.resolutionMetCount}</span><span className="itsd-stat-label">Resolution Met</span></div>
                        <div className="itsd-stat"><span className="itsd-stat-value color-red">{slaReport.resolutionBreachedCount}</span><span className="itsd-stat-label">Resolution Breached</span></div>
                    </div>
                    {slaReport.byPriority?.length > 0 && (
                        <table className="itsd-table">
                            <thead>
                                <tr><th>Priority</th><th>Count</th><th>Avg Response (min)</th><th>Avg Resolution (min)</th><th>Response Breach %</th><th>Resolution Breach %</th></tr>
                            </thead>
                            <tbody>
                                {slaReport.byPriority.map((row, i) => (
                                    <tr key={i}>
                                        <td>{row.priority}</td>
                                        <td>{row.count}</td>
                                        <td>{row.avgResponseMinutes?.toFixed(0) ?? '-'}</td>
                                        <td>{row.avgResolutionMinutes?.toFixed(0) ?? '-'}</td>
                                        <td>{row.responseBreachPercent?.toFixed(1)}%</td>
                                        <td>{row.resolutionBreachPercent?.toFixed(1)}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {showForm && (
                <div className="itsd-form-card">
                    <h3>{editingId ? 'Edit SLA Policy' : 'New SLA Policy'}</h3>
                    <div className="itsd-form-grid">
                        <label>Name *
                            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                        </label>
                        <label>Description
                            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                        </label>
                    </div>
                    <h4>Response Time Targets</h4>
                    <div className="itsd-form-grid four-col">
                        {PRIORITY_OPTIONS.map(p => (
                            <label key={p}>{p} (min)
                                <input type="number" value={form[`responseTime${p}Minutes`]}
                                    onChange={e => setForm(f => ({ ...f, [`responseTime${p}Minutes`]: parseInt(e.target.value) || 0 }))} />
                            </label>
                        ))}
                    </div>
                    <h4>Resolution Time Targets</h4>
                    <div className="itsd-form-grid four-col">
                        {PRIORITY_OPTIONS.map(p => (
                            <label key={p}>{p} (min)
                                <input type="number" value={form[`resolutionTime${p}Minutes`]}
                                    onChange={e => setForm(f => ({ ...f, [`resolutionTime${p}Minutes`]: parseInt(e.target.value) || 0 }))} />
                            </label>
                        ))}
                    </div>
                    <h4>Business Hours</h4>
                    <div className="itsd-form-grid">
                        <label className="itsd-checkbox-label">
                            <input type="checkbox" checked={form.businessHoursOnly}
                                onChange={e => setForm(f => ({ ...f, businessHoursOnly: e.target.checked }))} />
                            Business Hours Only
                        </label>
                        <label>Start Time
                            <input type="time" value={form.businessHoursStart}
                                onChange={e => setForm(f => ({ ...f, businessHoursStart: e.target.value }))} />
                        </label>
                        <label>End Time
                            <input type="time" value={form.businessHoursEnd}
                                onChange={e => setForm(f => ({ ...f, businessHoursEnd: e.target.value }))} />
                        </label>
                        <label>Business Days
                            <input value={form.businessDays} placeholder="1,2,3,4,5"
                                onChange={e => setForm(f => ({ ...f, businessDays: e.target.value }))} />
                        </label>
                    </div>
                    <h4>Escalation &amp; Warnings</h4>
                    <div className="itsd-form-grid">
                        <label className="itsd-checkbox-label">
                            <input type="checkbox" checked={form.autoEscalateOnBreach}
                                onChange={e => setForm(f => ({ ...f, autoEscalateOnBreach: e.target.checked }))} />
                            Auto-Escalate on Breach
                        </label>
                        <label className="itsd-checkbox-label">
                            <input type="checkbox" checked={form.notifyOnWarning}
                                onChange={e => setForm(f => ({ ...f, notifyOnWarning: e.target.checked }))} />
                            Notify on Warning
                        </label>
                        <label>Warning Threshold (%)
                            <input type="number" min="0" max="100" value={form.warningThresholdPercent}
                                onChange={e => setForm(f => ({ ...f, warningThresholdPercent: parseInt(e.target.value) || 75 }))} />
                        </label>
                        <label className="itsd-checkbox-label">
                            <input type="checkbox" checked={form.isDefault}
                                onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))} />
                            Default Policy
                        </label>
                    </div>
                    <div className="itsd-form-actions">
                        <button className="itsd-btn primary" onClick={handleSave} disabled={saving}>
                            {saving ? <FaSpinner className="spin" /> : <FaSave />} Save
                        </button>
                        <button className="itsd-btn" onClick={() => setShowForm(false)}><FaTimes /> Cancel</button>
                    </div>
                </div>
            )}

            <div className="itsd-card-list">
                {policies.map(p => (
                    <div key={p.policyId} className={`itsd-policy-card ${p.isDefault ? 'is-default' : ''}`}>
                        <div className="itsd-policy-header" onClick={() => setExpandedId(expandedId === p.policyId ? null : p.policyId)}>
                            <div className="itsd-policy-title">
                                <FaShieldAlt />
                                <span>{p.name}</span>
                                {p.isDefault && <span className="itsd-badge default">Default</span>}
                                {!p.isActive && <span className="itsd-badge inactive">Inactive</span>}
                            </div>
                            <div className="itsd-policy-actions">
                                <button title="Edit" onClick={e => { e.stopPropagation(); openEdit(p); }}><FaEdit /></button>
                                {expandedId === p.policyId ? <FaChevronDown /> : <FaChevronRight />}
                            </div>
                        </div>
                        {expandedId === p.policyId && (
                            <div className="itsd-policy-detail">
                                {p.description && <p className="itsd-policy-desc">{p.description}</p>}
                                <table className="itsd-table compact">
                                    <thead><tr><th>Priority</th><th>Response Target</th><th>Resolution Target</th></tr></thead>
                                    <tbody>
                                        {PRIORITY_OPTIONS.map(pr => (
                                            <tr key={pr}>
                                                <td>{pr}</td>
                                                <td>{formatMinutes(p[`responseTime${pr}Minutes`])}</td>
                                                <td>{formatMinutes(p[`resolutionTime${pr}Minutes`])}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div className="itsd-policy-meta">
                                    <span><FaClock /> {p.businessHoursOnly ? `Business hours: ${p.businessHoursStart} – ${p.businessHoursEnd}` : '24/7'}</span>
                                    {p.autoEscalateOnBreach && <span><FaExclamationTriangle /> Auto-escalate on breach</span>}
                                    {p.notifyOnWarning && <span>Warning at {p.warningThresholdPercent}%</span>}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
                {policies.length === 0 && <p className="itsd-empty">No SLA policies configured.</p>}
            </div>
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════════
// KNOWLEDGE BASE TAB
// ════════════════════════════════════════════════════════════════════════════════

const KnowledgeBaseTab = ({ apiFetch }) => {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyKbForm());
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [previewMode, setPreviewMode] = useState(false);
    const [viewArticle, setViewArticle] = useState(null);
    const [categories, setCategories] = useState([]);

    const loadArticles = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (statusFilter) params.append('status', statusFilter);
            const res = await apiFetch(`/api/ITServiceDesk/knowledge-base?${params}`);
            if (res.ok) {
                const json = await res.json();
                if (json.success) setArticles(json.data || []);
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to load articles');
        }
        finally { setLoading(false); }
    }, [apiFetch, search, statusFilter]);

    const loadCategories = useCallback(async () => {
        try {
            const res = await apiFetch('/api/ITServiceDesk/categories');
            if (res.ok) {
                const json = await res.json();
                if (json.success) setCategories(json.data || []);
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            /* silent */
        }
    }, [apiFetch]);

    useEffect(() => { loadArticles(); loadCategories(); }, [loadArticles, loadCategories]);

    const viewFullArticle = async (article) => {
        try {
            const res = await apiFetch(`/api/ITServiceDesk/knowledge-base/${article.articleId}`);
            if (res.ok) {
                const json = await res.json();
                if (json.success) {
                    const d = json.data;
                    setViewArticle({
                        ...d,
                        content: d.contentMarkdown || '',
                        visibility: d.isPublic ? 'Public' : 'Internal',
                        categoryName: d.categoryPath
                    });
                    return;
                }
            }
            toast.error('Failed to load article');
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to load article');
        }
    };

    const openCreate = () => { setEditingId(null); setForm(emptyKbForm()); setPreviewMode(false); setShowForm(true); };
    const openEdit = async (a) => {
        try {
            const res = await apiFetch(`/api/ITServiceDesk/knowledge-base/${a.articleId}`);
            if (res.ok) {
                const json = await res.json();
                if (json.success) {
                    const d = json.data;
                    setEditingId(d.articleId);
                    setForm({
                        title: d.title || '', content: d.contentMarkdown || '', summary: d.summary || '',
                        categoryId: d.categoryId, tags: d.tags || '',
                        visibility: d.isPublic ? 'Public' : 'Internal', status: d.status || 'Draft'
                    });
                    setPreviewMode(false);
                    setShowForm(true);
                    return;
                }
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            /* fall through */
        }
        // Fallback: use list data (content will be empty)
        setEditingId(a.articleId);
        setForm({
            title: a.title || '', content: '', summary: a.summary || '',
            categoryId: a.categoryId, tags: a.tags || '',
            visibility: a.isPublic ? 'Public' : 'Internal', status: a.status || 'Draft'
        });
        setPreviewMode(false);
        setShowForm(true);
    };

    const handleSave = async () => {
        if (!form.title.trim() || !form.content.trim()) { toast.error('Title and content are required'); return; }
        setSaving(true);
        try {
            const url = editingId ? `/api/ITServiceDesk/knowledge-base/${editingId}` : '/api/ITServiceDesk/knowledge-base';
            const method = editingId ? 'PUT' : 'POST';
            // Map frontend field names to backend DTO field names
            const payload = editingId
                ? { title: form.title, summary: form.summary, contentMarkdown: form.content, categoryId: form.categoryId, tags: form.tags, isPublic: form.visibility === 'Public', status: form.status }
                : { title: form.title, summary: form.summary, contentMarkdown: form.content, categoryId: form.categoryId, tags: form.tags, isPublic: form.visibility === 'Public', publish: form.status === 'Published' };
            const res = await apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (res.ok) {
                toast.success(editingId ? 'Article updated' : 'Article created');
                setShowForm(false);
                loadArticles();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.message || 'Save failed');
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Save failed');
        }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!confirm('Delete this article?')) return;
        try {
            const res = await apiFetch(`/api/ITServiceDesk/knowledge-base/${id}`, { method: 'DELETE' });
            if (res.ok) { toast.success('Article deleted'); loadArticles(); }
            else { toast.error('Delete failed'); }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Delete failed');
        }
    };

    const insertToolbar = (syntax) => {
        const textarea = document.getElementById('kb-content-editor');
        if (!textarea) return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selected = form.content.substring(start, end);
        let replacement;
        switch (syntax) {
            case 'bold': replacement = `**${selected || 'text'}**`; break;
            case 'italic': replacement = `*${selected || 'text'}*`; break;
            case 'code': replacement = `\`${selected || 'code'}\``; break;
            case 'h2': replacement = `## ${selected || 'Heading'}`; break;
            case 'ul': replacement = `- ${selected || 'item'}`; break;
            case 'ol': replacement = `1. ${selected || 'item'}`; break;
            case 'quote': replacement = `> ${selected || 'quote'}`; break;
            case 'hr': replacement = '\n---\n'; break;
            default: return;
        }
        const newContent = form.content.substring(0, start) + replacement + form.content.substring(end);
        setForm(f => ({ ...f, content: newContent }));
    };

    if (loading) return <HexagonLoader />;

    return (
        <div className="itsd-section">
            <div className="itsd-section-toolbar">
                <div className="itsd-search-box">
                    <FaSearch />
                    <input placeholder="Search articles..." value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <select className="itsd-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="">All Status</option>
                    <option value="Draft">Draft</option>
                    <option value="Published">Published</option>
                    <option value="Archived">Archived</option>
                </select>
                <button className="itsd-btn primary" onClick={openCreate}>
                    <FaPlus /> New Article
                </button>
            </div>

            {viewArticle && (
                <div className="itsd-article-view">
                    <div className="itsd-article-view-header">
                        <h2>{viewArticle.title}</h2>
                        <button className="itsd-btn-icon" onClick={() => setViewArticle(null)}><FaTimes /></button>
                    </div>
                    <div className="itsd-article-meta">
                        {viewArticle.visibility === 'Public' ? <span><FaGlobe /> Public</span> : <span><FaLock /> Internal</span>}
                        <span className={`itsd-badge ${viewArticle.status?.toLowerCase()}`}>{viewArticle.status}</span>
                        {viewArticle.categoryName && <span><FaFolder /> {viewArticle.categoryName}</span>}
                        <span>v{viewArticle.version}</span>
                        <span><FaStar /> {viewArticle.helpfulCount} helpful</span>
                    </div>
                    <div className="itsd-markdown-body">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{viewArticle.content}</ReactMarkdown>
                    </div>
                </div>
            )}

            {showForm && (
                <div className="itsd-form-card kb-form">
                    <h3>{editingId ? 'Edit Article' : 'New Article'}</h3>
                    <div className="itsd-form-grid">
                        <label>Title *
                            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
                        </label>
                        <label>Summary
                            <input value={form.summary} onChange={e => setForm(f => ({ ...f, summary: e.target.value }))} />
                        </label>
                        <label>Category
                            <select value={form.categoryId || ''} onChange={e => setForm(f => ({ ...f, categoryId: e.target.value ? parseInt(e.target.value) : null }))}>
                                <option value="">None</option>
                                {categories.map(c => <option key={c.categoryId} value={c.categoryId}>{c.fullPath || c.name}</option>)}
                            </select>
                        </label>
                        <label>Tags (comma separated)
                            <input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} />
                        </label>
                        <label>Visibility
                            <select value={form.visibility} onChange={e => setForm(f => ({ ...f, visibility: e.target.value }))}>
                                <option value="Internal">Internal</option>
                                <option value="Public">Public</option>
                            </select>
                        </label>
                        <label>Status
                            <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                                <option value="Draft">Draft</option>
                                <option value="Published">Published</option>
                                <option value="Archived">Archived</option>
                            </select>
                        </label>
                    </div>
                    <div className="itsd-editor-toolbar">
                        <button title="Bold" onClick={() => insertToolbar('bold')}><FaBold /></button>
                        <button title="Italic" onClick={() => insertToolbar('italic')}><FaItalic /></button>
                        <button title="Heading" onClick={() => insertToolbar('h2')}><FaHeading /></button>
                        <button title="Unordered List" onClick={() => insertToolbar('ul')}><FaListUl /></button>
                        <button title="Ordered List" onClick={() => insertToolbar('ol')}><FaListOl /></button>
                        <button title="Code" onClick={() => insertToolbar('code')}><FaCode /></button>
                        <button title="Quote" onClick={() => insertToolbar('quote')}><FaQuoteRight /></button>
                        <button title="Horizontal Rule" onClick={() => insertToolbar('hr')}><FaMinus /></button>
                        <span className="itsd-editor-divider" />
                        <button
                            className={previewMode ? 'active' : ''}
                            onClick={() => setPreviewMode(!previewMode)}
                            title={previewMode ? 'Edit' : 'Preview'}
                        >
                            {previewMode ? <FaPen /> : <FaEye />}
                            {previewMode ? ' Edit' : ' Preview'}
                        </button>
                    </div>
                    {previewMode ? (
                        <div className="itsd-markdown-body preview-box">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.content}</ReactMarkdown>
                        </div>
                    ) : (
                        <textarea
                            id="kb-content-editor"
                            className="itsd-content-editor"
                            rows={16}
                            value={form.content}
                            onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                            placeholder="Write your article content in Markdown..."
                        />
                    )}
                    <div className="itsd-form-actions">
                        <button className="itsd-btn primary" onClick={handleSave} disabled={saving}>
                            {saving ? <FaSpinner className="spin" /> : <FaSave />} Save
                        </button>
                        <button className="itsd-btn" onClick={() => setShowForm(false)}><FaTimes /> Cancel</button>
                    </div>
                </div>
            )}

            <div className="itsd-article-list">
                {articles.map(a => (
                    <div key={a.articleId} className="itsd-article-card" onClick={() => viewFullArticle(a)}>
                        <div className="itsd-article-title">
                            <FaBook />
                            <span>{a.title}</span>
                            <span className={`itsd-badge ${a.status?.toLowerCase()}`}>{a.status}</span>
                            {a.isPublic ? <FaGlobe title="Public" /> : <FaLock title="Internal" />}
                        </div>
                        {a.summary && <p className="itsd-article-summary">{a.summary}</p>}
                        <div className="itsd-article-footer">
                            {(a.categoryPath || a.categoryName) && <span><FaFolder /> {a.categoryPath || a.categoryName}</span>}
                            <span>v{a.version}</span>
                            <span><FaStar /> {a.helpfulCount}</span>
                            <span>{a.viewCount} views</span>
                            <div className="itsd-article-actions" onClick={e => e.stopPropagation()}>
                                <button title="Edit" onClick={() => openEdit(a)}><FaEdit /></button>
                                <button title="Delete" className="danger" onClick={() => handleDelete(a.articleId)}><FaTrash /></button>
                            </div>
                        </div>
                    </div>
                ))}
                {articles.length === 0 && <p className="itsd-empty">No articles found.</p>}
            </div>
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════════
// CSAT REPORTS TAB
// ════════════════════════════════════════════════════════════════════════════════

const CsatReportsTab = ({ apiFetch }) => {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState({ from: '', to: '' });

    const loadReport = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (dateRange.from) params.append('from', dateRange.from);
            if (dateRange.to) params.append('to', dateRange.to);
            const res = await apiFetch(`/api/ITServiceDesk/csat-report?${params}`);
            if (res.ok) {
                const json = await res.json();
                if (json.success) setReport(json.data);
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to load CSAT report');
        }
        finally { setLoading(false); }
    }, [apiFetch, dateRange]);

    useEffect(() => { loadReport(); }, [loadReport]);

    if (loading) return <HexagonLoader />;
    if (!report) return <p className="itsd-empty">No CSAT data available.</p>;

    const renderStars = (rating) => {
        return Array.from({ length: 5 }, (_, i) => (
            <FaStar key={i} className={i < Math.round(rating) ? 'star-filled' : 'star-empty'} />
        ));
    };

    return (
        <div className="itsd-section">
            <div className="itsd-section-toolbar">
                <div className="itsd-date-range">
                    <label>From: <input type="date" value={dateRange.from} onChange={e => setDateRange(r => ({ ...r, from: e.target.value }))} /></label>
                    <label>To: <input type="date" value={dateRange.to} onChange={e => setDateRange(r => ({ ...r, to: e.target.value }))} /></label>
                </div>
            </div>

            <div className="itsd-stats-row">
                <div className="itsd-stat">
                    <span className="itsd-stat-value">{report.totalSent}</span>
                    <span className="itsd-stat-label">Surveys Sent</span>
                </div>
                <div className="itsd-stat">
                    <span className="itsd-stat-value">{report.totalSubmitted}</span>
                    <span className="itsd-stat-label">Responses</span>
                </div>
                <div className="itsd-stat">
                    <span className="itsd-stat-value">{report.responseRate?.toFixed(0)}%</span>
                    <span className="itsd-stat-label">Response Rate</span>
                </div>
                <div className="itsd-stat">
                    <span className="itsd-stat-value csat-score">{report.averageRating?.toFixed(1) || '-'}</span>
                    <span className="itsd-stat-label">Avg Rating {renderStars(report.averageRating || 0)}</span>
                </div>
            </div>

            {report.byAgent?.length > 0 && (
                <>
                    <h3>By Agent</h3>
                    <table className="itsd-table">
                        <thead><tr><th>Agent</th><th>Surveys</th><th>Avg Rating</th></tr></thead>
                        <tbody>
                            {report.byAgent.map((row, i) => (
                                <tr key={i}>
                                    <td>{row.agentName || `User #${row.resolvedByUserId}`}</td>
                                    <td>{row.count}</td>
                                    <td>{renderStars(row.avgRating)} {row.avgRating?.toFixed(1)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {report.byPriority?.length > 0 && (
                <>
                    <h3>By Priority</h3>
                    <table className="itsd-table">
                        <thead><tr><th>Priority</th><th>Surveys</th><th>Avg Rating</th></tr></thead>
                        <tbody>
                            {report.byPriority.map((row, i) => (
                                <tr key={i}>
                                    <td>{row.priority}</td>
                                    <td>{row.count}</td>
                                    <td>{renderStars(row.avgRating)} {row.avgRating?.toFixed(1)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {report.recentFeedback?.length > 0 && (
                <>
                    <h3>Recent Feedback</h3>
                    <div className="itsd-feedback-list">
                        {report.recentFeedback.map((fb, i) => (
                            <div key={i} className="itsd-feedback-card">
                                <div className="itsd-feedback-stars">{renderStars(fb.rating)}</div>
                                <p className="itsd-feedback-text">{fb.feedback}</p>
                                <span className="itsd-feedback-date">{new Date(fb.submittedDate).toLocaleDateString()}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════════
// RECURRING TICKETS TAB
// ════════════════════════════════════════════════════════════════════════════════

const RecurringTicketsTab = ({ apiFetch }) => {
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyRecurringForm());
    const [saving, setSaving] = useState(false);
    const [categories, setCategories] = useState([]);
    const [itContexts, setItContexts] = useState([]);
    const [activeTemplate, setActiveTemplate] = useState(null);
    const [loadingTemplate, setLoadingTemplate] = useState(false);
    const templateFormRef = useRef({});

    const loadSchedules = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch('/api/ITServiceDesk/recurring-schedules');
            if (res.ok) {
                const json = await res.json();
                if (json.success) setSchedules(json.data || []);
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to load recurring schedules');
        }
        finally { setLoading(false); }
    }, [apiFetch]);

    const loadCategories = useCallback(async () => {
        try {
            const res = await apiFetch('/api/ITServiceDesk/categories');
            if (res.ok) {
                const json = await res.json();
                if (json.success) setCategories(json.data || []);
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            /* silent */
        }
    }, [apiFetch]);

    const loadItContexts = useCallback(async () => {
        try {
            const res = await apiFetch('/api/ModuleTemplateAssignments?moduleKey=IT');
            if (res.ok) {
                const json = await res.json();
                if (json.success && Array.isArray(json.data)) {
                    const contexts = json.data.flatMap(mod =>
                        (mod.contexts || []).filter(c => c.isActive).map(c => ({
                            contextKey: c.contextKey,
                            displayName: c.contextDisplayName,
                            templateKey: c.assignedTemplateKey,
                            defaultBoardId: c.defaultBoardId,
                            defaultDepartmentId: c.defaultDepartmentId
                        }))
                    );
                    setItContexts(contexts);
                }
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
        }
    }, [apiFetch]);

    useEffect(() => { loadSchedules(); loadCategories(); loadItContexts(); }, [loadSchedules, loadCategories, loadItContexts]);

    /** Load template schema when the selected context has a templateKey */
    const loadTemplateForKey = useCallback(async (templateKey) => {
        if (!templateKey) {
            setActiveTemplate(null);
            return;
        }
        setLoadingTemplate(true);
        try {
            const res = await apiFetch(`/api/Templates/${templateKey}`);
            const json = await res.json();
            if (res.ok && json.success && json.data) {
                setActiveTemplate(json.data);
            } else {
                setActiveTemplate(null);
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            setActiveTemplate(null);
        } finally {
            setLoadingTemplate(false);
        }
    }, [apiFetch]);

    /** When form.actionItemsType changes, resolve context defaults and load template */
    const handleContextChange = useCallback((contextKey) => {
        const ctx = itContexts.find(c => c.contextKey === contextKey);
        setForm(f => ({
            ...f,
            actionItemsType: contextKey,
            templateKey: ctx?.templateKey || '',
            boardId: ctx?.defaultBoardId || f.boardId,
            assignToDepartmentId: ctx?.defaultDepartmentId || f.assignToDepartmentId
        }));
        templateFormRef.current = {};
        loadTemplateForKey(ctx?.templateKey || null);
    }, [itContexts, loadTemplateForKey]);

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyRecurringForm());
        setActiveTemplate(null);
        templateFormRef.current = {};
        setShowForm(true);
    };

    const openEdit = (s) => {
        setEditingId(s.scheduleId);
        const savedTemplateData = s.templateDataJson ? JSON.parse(s.templateDataJson) : {};
        setForm({
            name: s.name, description: s.description || '',
            cronExpression: s.cronExpression, timeZone: s.timeZone,
            propertyId: s.propertyId,
            ticketTitle: s.ticketTitle, ticketDescription: s.ticketDescription || '',
            ticketPriority: s.ticketPriority, actionItemsType: s.actionItemsType,
            templateKey: s.templateKey || '', categoryId: s.categoryId,
            assignToUserId: s.assignToUserId, assignToDepartmentId: s.assignToDepartmentId,
            assignToFunctionId: s.assignToFunctionId, boardId: s.boardId,
            tags: s.tags || '', dueDaysFromCreation: s.dueDaysFromCreation,
            endDate: s.endDate ? s.endDate.substring(0, 10) : null,
            templateDataJson: s.templateDataJson || null
        });
        templateFormRef.current = savedTemplateData;
        loadTemplateForKey(s.templateKey || null);
        setShowForm(true);
    };

    const handleSave = async () => {
        if (!form.name.trim() || !form.ticketTitle.trim() || !form.cronExpression.trim()) {
            toast.error('Name, ticket title, and cron expression are required');
            return;
        }
        setSaving(true);
        try {
            // Merge template pre-fill data from the TemplateRenderer into the payload
            const templateData = templateFormRef.current && Object.keys(templateFormRef.current).length > 0
                ? JSON.stringify(templateFormRef.current)
                : form.templateDataJson || null;

            const payload = { ...form, templateDataJson: templateData };

            const url = editingId ? `/api/ITServiceDesk/recurring-schedules/${editingId}` : '/api/ITServiceDesk/recurring-schedules';
            const method = editingId ? 'PUT' : 'POST';
            const res = await apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (res.ok) {
                toast.success(editingId ? 'Schedule updated' : 'Schedule created');
                setShowForm(false);
                loadSchedules();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.message || 'Save failed');
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Save failed');
        }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!confirm('Delete this recurring schedule?')) return;
        try {
            const res = await apiFetch(`/api/ITServiceDesk/recurring-schedules/${id}`, { method: 'DELETE' });
            if (res.ok) { toast.success('Schedule deleted'); loadSchedules(); }
            else { toast.error('Delete failed'); }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Delete failed');
        }
    };

    const handleToggle = async (s) => {
        try {
            const res = await apiFetch(`/api/ITServiceDesk/recurring-schedules/${s.scheduleId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...s, isActive: !s.isActive })
            });
            if (res.ok) { toast.success(s.isActive ? 'Schedule paused' : 'Schedule activated'); loadSchedules(); }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Toggle failed');
        }
    };

    /** Resolve context display name for schedule cards */
    const getContextDisplayName = (actionItemsType) => {
        const ctx = itContexts.find(c => c.contextKey === actionItemsType);
        return ctx?.displayName || actionItemsType;
    };

    if (loading) return <HexagonLoader />;

    return (
        <div className="itsd-section">
            <div className="itsd-section-toolbar">
                <button className="itsd-btn primary" onClick={openCreate}>
                    <FaPlus /> New Schedule
                </button>
            </div>

            {showForm && (
                <div className="itsd-form-card">
                    <h3>{editingId ? 'Edit Recurring Schedule' : 'New Recurring Schedule'}</h3>
                    <div className="itsd-form-grid">
                        <label>Schedule Name *
                            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                        </label>
                        <label>Description
                            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                        </label>
                        <label>Cron Expression *
                            <input value={form.cronExpression} placeholder="0 9 * * 1"
                                onChange={e => setForm(f => ({ ...f, cronExpression: e.target.value }))} />
                            <small>Format: minute hour day-of-month month day-of-week</small>
                        </label>
                        <label>Timezone
                            <input value={form.timeZone} onChange={e => setForm(f => ({ ...f, timeZone: e.target.value }))} />
                        </label>
                    </div>
                    <h4>Ticket Template</h4>
                    <div className="itsd-form-grid">
                        <label>Ticket Title *
                            <input value={form.ticketTitle} placeholder="Weekly {Date} check"
                                onChange={e => setForm(f => ({ ...f, ticketTitle: e.target.value }))} />
                            <small>Placeholders: {'{Date}'}, {'{Month}'}, {'{Year}'}, {'{Week}'}, {'{Counter}'}</small>
                        </label>
                        <label>Ticket Description
                            <textarea rows={3} value={form.ticketDescription}
                                onChange={e => setForm(f => ({ ...f, ticketDescription: e.target.value }))} />
                        </label>
                        <label>Priority
                            <select value={form.ticketPriority} onChange={e => setForm(f => ({ ...f, ticketPriority: e.target.value }))}>
                                {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </label>
                        <label>Type
                            <select value={form.actionItemsType} onChange={e => handleContextChange(e.target.value)}>
                                <option value="">-- Select Type --</option>
                                {itContexts.map(ctx => (
                                    <option key={ctx.contextKey} value={ctx.contextKey}>{ctx.displayName}</option>
                                ))}
                            </select>
                        </label>
                        <label>Category
                            <select value={form.categoryId || ''} onChange={e => setForm(f => ({ ...f, categoryId: e.target.value ? parseInt(e.target.value) : null }))}>
                                <option value="">None</option>
                                {categories.map(c => <option key={c.categoryId} value={c.categoryId}>{c.fullPath || c.name}</option>)}
                            </select>
                        </label>
                        <label>Due Days From Creation
                            <input type="number" min="0" value={form.dueDaysFromCreation || ''}
                                onChange={e => setForm(f => ({ ...f, dueDaysFromCreation: e.target.value ? parseInt(e.target.value) : null }))} />
                        </label>
                        <label>Tags
                            <input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} />
                        </label>
                        <label>End Date (optional)
                            <input type="date" value={form.endDate || ''}
                                onChange={e => setForm(f => ({ ...f, endDate: e.target.value || null }))} />
                        </label>
                    </div>

                    {/* Template preview — rendered when the selected type has an assigned template.
                        Pre-fill with saved template data when editing an existing schedule. */}
                    {loadingTemplate && (
                        <div style={{ padding: '12px', textAlign: 'center' }}>
                            <FaSpinner className="spin" /> Loading template fields...
                        </div>
                    )}
                    {!loadingTemplate && activeTemplate && (
                        <div style={{ marginTop: '16px' }}>
                            <h4>Template Fields ({activeTemplate.displayName || activeTemplate.templateKey})</h4>
                            <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0 0 8px 0' }}>
                                Pre-fill these fields to set default values for each generated ticket.
                                Placeholders like {'{Date}'}, {'{Counter}'} in text fields will be resolved when the ticket is created.
                            </p>
                            <TemplateRenderer
                                key={`${activeTemplate.templateKey || activeTemplate.templateId}-${editingId || 'new'}`}
                                template={activeTemplate}
                                initialData={templateFormRef.current || {}}
                                onSubmit={null}
                                onCancel={null}
                                readOnly={false}
                                onChange={(data) => { templateFormRef.current = data; }}
                            />
                        </div>
                    )}

                    <div className="itsd-form-actions">
                        <button className="itsd-btn primary" onClick={handleSave} disabled={saving}>
                            {saving ? <FaSpinner className="spin" /> : <FaSave />} Save
                        </button>
                        <button className="itsd-btn" onClick={() => setShowForm(false)}><FaTimes /> Cancel</button>
                    </div>
                </div>
            )}

            <div className="itsd-card-list">
                {schedules.map(s => (
                    <div key={s.scheduleId} className={`itsd-schedule-card ${!s.isActive ? 'paused' : ''}`}>
                        <div className="itsd-schedule-header">
                            <div className="itsd-schedule-title">
                                <FaSync className={s.isActive ? 'color-green' : ''} />
                                <span>{s.name}</span>
                                {!s.isActive && <span className="itsd-badge inactive">Paused</span>}
                            </div>
                            <div className="itsd-schedule-actions">
                                <button title={s.isActive ? 'Pause' : 'Activate'} onClick={() => handleToggle(s)}>
                                    {s.isActive ? <FaTimes /> : <FaCheckCircle />}
                                </button>
                                <button title="Edit" onClick={() => openEdit(s)}><FaEdit /></button>
                                <button title="Delete" className="danger" onClick={() => handleDelete(s.scheduleId)}><FaTrash /></button>
                            </div>
                        </div>
                        <div className="itsd-schedule-detail">
                            <span><FaClock /> {s.cronExpression}</span>
                            <span>Type: {getContextDisplayName(s.actionItemsType)}</span>
                            <span>Priority: {s.ticketPriority}</span>
                            <span>Runs: {s.runCount}</span>
                            {s.lastRunDate && <span>Last: {new Date(s.lastRunDate).toLocaleString()}</span>}
                            {s.nextRunDate && <span>Next: {new Date(s.nextRunDate).toLocaleString()}</span>}
                        </div>
                        <p className="itsd-schedule-template">→ {s.ticketTitle}</p>
                    </div>
                ))}
                {schedules.length === 0 && <p className="itsd-empty">No recurring schedules configured.</p>}
            </div>
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════════
// EMAIL ACCOUNTS TAB (ITSM-specific mailbox view)
// ════════════════════════════════════════════════════════════════════════════════

const EmailAccountsTab = ({ apiFetch }) => {
    const [mailboxes, setMailboxes] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadMailboxes = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch('/api/EmailMailboxes/by-functional-area/ITServiceManagement');
            if (res.ok) {
                const json = await res.json();
                if (json.success) setMailboxes(json.data || []);
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to load ITSM email accounts');
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => { loadMailboxes(); }, [loadMailboxes]);

    if (loading) return <p className="itsd-empty"><FaSpinner className="fa-spin" /> Loading email accounts...</p>;

    return (
        <div className="itsd-section">
            <div className="itsd-section-header">
                <h2><FaEnvelope /> Email Accounts</h2>
                <p className="itsd-section-desc">
                    Mailboxes configured for IT Service Management. Inbound emails create tickets; outbound emails send notifications.
                    Manage all mailbox settings in <a href="/settings/email-mailboxes">Settings → Email Mailboxes</a>.
                </p>
            </div>

            {mailboxes.length === 0 ? (
                <div className="itsd-empty">
                    <p>No email accounts configured for IT Service Management.</p>
                    <p>
                        Go to <a href="/settings/email-mailboxes">Settings → Email Mailboxes</a> and create a mailbox with 
                        Functional Area set to <strong>IT Service Management</strong>.
                    </p>
                </div>
            ) : (
                <div className="itsd-list">
                    {mailboxes.map(mb => (
                        <div key={mb.id} className={`itsd-list-card ${mb.isActive ? '' : 'inactive'}`}>
                            <div className="itsd-list-card-header">
                                <strong>{mb.displayName || mb.emailAddress}</strong>
                                <span className={`itsd-badge ${mb.isActive ? 'success' : 'muted'}`}>
                                    {mb.isActive ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                            <div className="itsd-list-card-body">
                                <span><FaEnvelope /> {mb.emailAddress}</span>
                                <span>
                                    {mb.inboundEnabled && <span className="itsd-badge info" style={{ marginRight: 4 }}>Inbound</span>}
                                    {mb.outboundEnabled && <span className="itsd-badge success" style={{ marginRight: 4 }}>Outbound</span>}
                                    {mb.autoResponseEnabled && <span className="itsd-badge warning">Auto-Response</span>}
                                </span>
                                <span>Action Type: {mb.actionItemType}</span>
                                <span>Priority: {mb.defaultPriority}</span>
                                {mb.lastCheckedAt && <span>Last checked: {new Date(mb.lastCheckedAt).toLocaleString()}</span>}
                                {mb.lastError && <span className="itsd-error-text"><FaExclamationTriangle /> {mb.lastError}</span>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ITServiceDeskPage;
